import { useEffect, useState, useMemo, lazy, Suspense } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useSearchParams,
} from 'react-router-dom'
import { useCollaboration } from './hooks/useCollaboration'
import { useTokenRefresh } from './hooks/useTokenRefresh'
import { useAppStore } from './stores/useAppStore'
import { ToastProvider } from './components/ui/Toast'
import LayoutShell from './components/LayoutShell'
import { getRoom } from './services/rooms'
import LoginPage from './pages/LoginPage'
import RegisterPage from './pages/RegisterPage'
import DashboardPage from './pages/DashboardPage'
import JoinPage from './pages/JoinPage'
import ResetPasswordPage from './pages/ResetPasswordPage'

const queryClient = new QueryClient()

// O Studio (React Flow + ELK) só é baixado quando um projeto Studio é aberto.
const StudioShell = lazy(() => import('./studio/StudioShell'))

const opening = <div className="h-screen flex items-center justify-center text-faint text-sm bg-canvas">Abrindo projeto…</div>

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const token = localStorage.getItem('token')
  return token ? <>{children}</> : <Navigate to="/login" replace />
}

function AppInner() {
  const { user, setUser, setRoom } = useAppStore()
  const [token, setToken] = useState<string>(() => localStorage.getItem('token') || '')
  useTokenRefresh()
  const [roomId] = useState<string>(() => new URLSearchParams(window.location.search).get('room') || '')

  useEffect(() => {
    const storedToken = localStorage.getItem('token')
    if (storedToken) {
      setToken(storedToken)
    }

    const storedUser = localStorage.getItem('user')
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser))
      } catch {
        // ignore parse error
      }
    }

    const storedRoom = localStorage.getItem('currentRoom')
    if (storedRoom) {
      try {
        setRoom(JSON.parse(storedRoom))
      } catch {
        // ignore parse error
      }
    }

    const storedDark = localStorage.getItem('darkMode')
    if (storedDark === 'true') {
      document.documentElement.classList.add('dark')
      useAppStore.setState({ darkMode: true })
    }
  }, [setUser, setRoom])

  const collabUser = useMemo(() => {
    return user ? { id: user.id, name: user.name, email: user.email } : null
  }, [user?.id, user?.name, user?.email])

  const collaboration = useCollaboration(roomId, token, collabUser)

  useEffect(() => {
    if (collaboration) {
      useAppStore.setState({ usersOnline: collaboration.users })
    }
  }, [collaboration?.users])

  // Tipo da sala decide o shell: 'studio' (diagrama com agentes) ou o fluxo de ideias.
  const storedType = (() => {
    const stored = useAppStore.getState().currentRoom
    return stored?.id === roomId ? stored.room_type : undefined
  })()
  const [roomType, setRoomType] = useState<string | undefined>(storedType)
  useEffect(() => {
    if (!roomId) return
    let alive = true
    getRoom(roomId)
      .then((room) => {
        if (!alive) return
        setRoomType(room.room_type || 'whiteboard')
        const stored = useAppStore.getState().currentRoom
        const merged = { ...(stored?.id === room.id ? stored : {}), ...room }
        localStorage.setItem('currentRoom', JSON.stringify(merged))
        setRoom(merged as typeof stored & typeof room)
      })
      .catch(() => alive && setRoomType((current) => current || 'whiteboard'))
    return () => {
      alive = false
    }
  }, [roomId, setRoom])

  if (!roomType) return opening
  if (roomType === 'studio') {
    return (
      <Suspense fallback={opening}>
        <StudioShell collaboration={collaboration} roomId={roomId} />
      </Suspense>
    )
  }
  return <LayoutShell collaboration={collaboration} roomId={roomId} />
}

function HomeRoute() {
  const [params] = useSearchParams()
  const token = localStorage.getItem('token')
  if (!token) return <Navigate to="/login" replace />
  if (!params.get('room')) return <Navigate to="/dashboard" replace />
  // key: trocar de sala pela URL remonta a colaboração (o roomId é lido uma vez).
  return <AppInner key={params.get('room')} />
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ToastProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/join/:token" element={<JoinPage />} />
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <DashboardPage />
              </PrivateRoute>
            }
          />
          <Route path="/" element={<HomeRoute />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
        </ToastProvider>
      </BrowserRouter>
    </QueryClientProvider>
  )
}

export default App
